import { useEffect, useMemo, useState } from 'react';
import {
  TeCheckbox,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TePlaceholder,
  TeText,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import { useEula } from './useEula';
import EulaContent from './EulaContent';
import { GetEULAStatus } from '../../api/ExperienceFragment/query';

interface EulaLayoutProps {
  onChecked?: (checked: boolean) => void;
  termsAndConditionChecked?: boolean;
  legalDisclaimerChecked?: boolean;
  onTermsAndConditionChange?: (checked: boolean) => void;
  onLegalDisclaimerChange?: (checked: boolean) => void;
}

type DisclaimerStatus = {
  legalDisclaimer: boolean;
  termsAndCondition: boolean;
};

const INITIAL_DISCLAIMER_STATE: DisclaimerStatus = {
  legalDisclaimer: false,
  termsAndCondition: false,
};

const EULA_MAX_HEIGHT = 300;

const EulaView = ({
  onChecked,
  termsAndConditionChecked: externalTermsAndCondition,
  legalDisclaimerChecked: externalLegalDisclaimer,
  onTermsAndConditionChange,
  onLegalDisclaimerChange,
}: EulaLayoutProps) => {
  const { t } = useTranslation('common');

  const eula = useEula();
  const { content, status } = eula;

  const domains = useMemo(() => content?.domains ?? [], [content]);
  const isControlled =
    externalTermsAndCondition !== undefined && externalLegalDisclaimer !== undefined;
  const [disclaimerChecked, setDisclaimerChecked] =
    useState<DisclaimerStatus>(INITIAL_DISCLAIMER_STATE);

  const currentDisclaimerState = isControlled
    ? {
        termsAndCondition: externalTermsAndCondition!,
        legalDisclaimer: externalLegalDisclaimer!,
      }
    : disclaimerChecked;

  useEffect(() => {
    if (!domains.length && !isControlled) {
      setDisclaimerChecked(INITIAL_DISCLAIMER_STATE);
    }
  }, [domains, isControlled]);

  const allDisclaimersChecked =
    currentDisclaimerState.legalDisclaimer && currentDisclaimerState.termsAndCondition;

  useEffect(() => {
    if (onChecked) {
      onChecked(allDisclaimersChecked);
    }
  }, [allDisclaimersChecked, onChecked]);

  const toggleDisclaimer = (key: keyof DisclaimerStatus) => {
    if (isControlled) {
      if (key === 'termsAndCondition' && onTermsAndConditionChange) {
        onTermsAndConditionChange(!currentDisclaimerState.termsAndCondition);
      } else if (key === 'legalDisclaimer' && onLegalDisclaimerChange) {
        onLegalDisclaimerChange(!currentDisclaimerState.legalDisclaimer);
      }
    } else {
      setDisclaimerChecked((prev) => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const disclaimerItems = [
    { key: 'termsAndCondition', label: t('eula.disclaimerTerms') },
    { key: 'legalDisclaimer', label: t('eula.disclaimerLegal') },
  ] as const;

  const isWaitingForInitialContent =
    (status === GetEULAStatus.LOADING || status === GetEULAStatus.IDLE) && !content;

  return (
    <TeContainer column fill overflow="hidden" padding={0} gap={0}>
      <TeContainerSlim column fill padding={['l', 'm']} gap="xs2">
        <TeText weight={700} size={16}>
          {t('termsAndConditions')}
        </TeText>
        <TeText weight={400} size={12} color="text.secondary">
          {t('serviceAgreement.agreementReview.termsAndConditionsDescription')}
        </TeText>
      </TeContainerSlim>

      <TeDivider dividerColor="border.secondary" />

      <TeContainer padding={0}>
        <TeContainer fill overflow="auto" padding={['l']} style={{ maxHeight: EULA_MAX_HEIGHT }}>
          <TeContainerSlim fill gap="s" column height={400}>
            <EulaContent {...eula} />
          </TeContainerSlim>
        </TeContainer>
      </TeContainer>

      <TeDivider dividerColor="border.secondary" />

      <TeContainerSlim column fill padding={['m']} gap="s" style={{ overflowY: 'auto' }}>
        {disclaimerItems.map(({ key, label }) =>
          isWaitingForInitialContent ? (
            <TeContainerSlim key={key} fill gap={'m'}>
              <TePlaceholder height={16} width={16} />
              <TePlaceholder height={16} width={700} />
            </TeContainerSlim>
          ) : (
            <TeContainerSlim key={key} gap="xs">
              <TeCheckbox
                checked={currentDisclaimerState[key]}
                techange={() => toggleDisclaimer(key)}
              />
              <TeContainerSlim grow>
                <TeText color="text.primary">{label}</TeText>
              </TeContainerSlim>
            </TeContainerSlim>
          )
        )}
      </TeContainerSlim>
    </TeContainer>
  );
};

export default EulaView;
